import { useState, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  UploadSimple,
  FileText,
  Check,
  X,
  Spinner,
  MagnifyingGlass,
  ArrowRight,
  DownloadSimple,
  Car,
  Users
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const VEHICLE_FIELDS = [
  { value: "marque", label: "Marque *" }, { value: "modele", label: "Modele *" }, { value: "version", label: "Version" },
  { value: "vin", label: "VIN" }, { value: "immatriculation", label: "Immatriculation" }, { value: "kilometrage", label: "Kilometrage" },
  { value: "carburant", label: "Carburant" }, { value: "boite", label: "Boite" }, { value: "annee", label: "Annee" },
  { value: "couleur", label: "Couleur" }, { value: "prix_achat", label: "Prix achat" }, { value: "prix_vente", label: "Prix vente" },
  { value: "", label: "-- Ignorer --" }
];

const CLIENT_FIELDS = [
  { value: "nom", label: "Nom *" }, { value: "prenom", label: "Prenom" }, { value: "telephone", label: "Telephone" },
  { value: "email", label: "Email" }, { value: "adresse", label: "Adresse" }, { value: "code_postal", label: "Code postal" },
  { value: "ville", label: "Ville" }, { value: "", label: "-- Ignorer --" }
];

export default function ImportCSV() {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1=upload, 2=mapping, 3=result
  const [typeImport, setTypeImport] = useState("vehicles");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fields = typeImport === "vehicles" ? VEHICLE_FIELDS : CLIENT_FIELDS;

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
  };

  const previewFile = async () => {
    if (!file) { toast.error("Selectionnez un fichier CSV"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/import/preview?type_import=${typeImport}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPreview(res.data);
      // Auto-map columns by name matching
      const autoMap = {};
      const colLower = (res.data.columns || []).map(c => c.toLowerCase().trim());
      for (const col of res.data.columns || []) {
        const cl = col.toLowerCase().trim();
        const match = fields.find(f => f.value && (f.value === cl || f.label.toLowerCase().includes(cl) || cl.includes(f.value)));
        autoMap[col] = match ? match.value : "";
      }
      setMapping(autoMap);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lecture fichier");
    }
    setLoading(false);
  };

  const executeImport = async () => {
    const mapped = Object.entries(mapping).filter(([, v]) => v);
    if (mapped.length === 0) { toast.error("Mappez au moins une colonne"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/import/execute?type_import=${typeImport}&mapping=${encodeURIComponent(JSON.stringify(mapping))}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(res.data);
      setStep(3);
      if (res.data.imported > 0) toast.success(`${res.data.imported} enregistrement(s) importe(s) !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur import");
    }
    setLoading(false);
  };

  const reset = () => { setStep(1); setFile(null); setPreview(null); setMapping({}); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <div className="space-y-6" data-testid="import-csv-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Import CSV</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Importez vehicules ou clients en masse depuis un fichier CSV</p>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-sm">
        {[{ n: 1, l: "Upload" }, { n: 2, l: "Mapping" }, { n: 3, l: "Resultat" }].map(({ n, l }) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= n ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500 dark:bg-slate-700'}`}>{n}</div>
            <span className={`${step >= n ? 'text-slate-900 dark:text-slate-100 font-medium' : 'text-slate-400'}`}>{l}</span>
            {n < 3 && <ArrowRight size={14} className="text-slate-300 mx-1" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="card p-6 space-y-5">
          <div>
            <Label className="form-label mb-2">Type d'import</Label>
            <div className="flex gap-3">
              <button onClick={() => setTypeImport("vehicles")} className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${typeImport === "vehicles" ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`} data-testid="import-type-vehicles">
                <Car size={20} className={typeImport === "vehicles" ? "text-blue-600" : "text-slate-400"} />
                <span className="text-sm font-medium">Vehicules</span>
              </button>
              <button onClick={() => setTypeImport("clients")} className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${typeImport === "clients" ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`} data-testid="import-type-clients">
                <Users size={20} className={typeImport === "clients" ? "text-blue-600" : "text-slate-400"} />
                <span className="text-sm font-medium">Clients</span>
              </button>
            </div>
          </div>

          <div>
            <Label className="form-label mb-2">Fichier CSV</Label>
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <UploadSimple size={32} className="mx-auto text-slate-400 mb-3" />
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">Glissez votre fichier ou cliquez pour parcourir</p>
              <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFileSelect} className="hidden" data-testid="csv-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="csv-browse-btn">
                <FileText size={16} className="mr-1.5" /> Parcourir
              </Button>
              {file && <p className="mt-3 text-sm text-emerald-600 font-medium"><Check size={14} className="inline mr-1" />{file.name}</p>}
            </div>
          </div>

          <p className="text-xs text-slate-400">Formats acceptes: CSV (separateur ; ou ,). Encodage UTF-8 ou Latin-1. Max 1000 lignes.</p>

          <div className="flex justify-end">
            <Button onClick={previewFile} disabled={!file || loading} className="btn-primary" data-testid="csv-preview-btn">
              {loading ? <Spinner size={16} className="mr-1.5 animate-spin" /> : <MagnifyingGlass size={16} className="mr-1.5" />}
              {loading ? "Analyse..." : "Analyser le fichier"}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Mapping */}
      {step === 2 && preview && (
        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">Mapping des colonnes</h3>
            <p className="text-xs text-slate-500 mb-4">Associez chaque colonne CSV a un champ de la base</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(preview.columns || []).map(col => (
                <div key={col} className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded truncate min-w-[80px]">{col}</span>
                  <ArrowRight size={12} className="text-slate-400 shrink-0" />
                  <Select value={mapping[col] || ""} onValueChange={v => setMapping({ ...mapping, [col]: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`map-${col}`}><SelectValue placeholder="Ignorer" /></SelectTrigger>
                    <SelectContent>
                      {fields.map(f => <SelectItem key={f.value} value={f.value || "_ignore"}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview table */}
          <div className="card overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-700 text-white text-sm font-semibold">Apercu ({preview.total_rows} lignes)</div>
            <div className="overflow-x-auto max-h-64">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 dark:bg-slate-900">{(preview.columns || []).map(c => <th key={c} className="px-3 py-1.5 text-left font-medium text-slate-500">{c}</th>)}</tr></thead>
                <tbody>
                  {(preview.rows || []).slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                      {(preview.columns || []).map(c => <td key={c} className="px-3 py-1.5 text-slate-700 dark:text-slate-300 truncate max-w-[150px]">{row[c] || ""}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="csv-back-btn">Retour</Button>
            <Button onClick={executeImport} disabled={loading} className="btn-primary" data-testid="csv-import-btn">
              {loading ? <Spinner size={16} className="mr-1.5 animate-spin" /> : <DownloadSimple size={16} className="mr-1.5" />}
              {loading ? "Import en cours..." : `Importer ${preview.total_rows} lignes`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && result && (
        <div className="card p-6 space-y-4">
          <div className="text-center">
            {result.imported > 0 ? (
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3"><Check size={32} className="text-emerald-600" /></div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3"><X size={32} className="text-red-600" /></div>
            )}
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Import termine</h3>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
            <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{result.imported}</p>
              <p className="text-xs text-slate-500">Importe(s)</p>
            </div>
            <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-2xl font-bold text-red-500">{result.total_errors}</p>
              <p className="text-xs text-slate-500">Erreur(s)</p>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="max-h-40 overflow-y-auto">
              <h4 className="text-sm font-medium text-red-600 mb-2">Erreurs :</h4>
              {result.errors.map((err, i) => (
                <p key={i} className="text-xs text-red-500 py-1 border-b border-red-100">Ligne {err.line}: {err.error}</p>
              ))}
            </div>
          )}

          <div className="flex justify-center">
            <Button onClick={reset} className="btn-primary" data-testid="csv-reset-btn">Nouvel import</Button>
          </div>
        </div>
      )}
    </div>
  );
}
